/* eslint-disable react/prop-types */
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, FileDown, Loader2 } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { toast } from "../ui/sonner";
import DatePickerInput from "../DatePickerInput";
import SearchableMultiSelect from "../SearchableMultiSelect";

const DIMS = [["person", "Person"], ["project", "Project"], ["date", "Date"]];
const DIM_LABEL = Object.fromEntries(DIMS);
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const money = (v) => (v === null || v === undefined ? "—" : `${v < 0 ? "-" : ""}R ${Math.abs(v).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

function presets() {
  const n = new Date();
  const first = new Date(n.getFullYear(), n.getMonth(), 1);
  const lastPrevEnd = new Date(n.getFullYear(), n.getMonth(), 0);
  const lastPrevStart = new Date(n.getFullYear(), n.getMonth() - 1, 1);
  return [
    ["This month", iso(first), iso(n)],
    ["Last month", iso(lastPrevStart), iso(lastPrevEnd)],
    ["Last 3 months", iso(new Date(n.getFullYear(), n.getMonth() - 2, 1)), iso(n)],
    ["This year", `${n.getFullYear()}-01-01`, iso(n)],
  ];
}

const Totals = ({ t, bold }) => (
  <>
    <td className="text-right font-mono px-2 text-white/70">{money(t.budget)}</td>
    <td className="text-right font-mono px-2 text-rose-300">{money(t.penalties)}</td>
    <td className="text-right font-mono px-2 text-emerald-300">{money(t.rewards)}</td>
    <td className={`text-right font-mono px-2 ${bold ? "font-bold" : "font-semibold"} ${t.balance < 0 ? "text-rose-300" : "text-white"}`}>{money(t.balance)}</td>
  </>
);

function NodeRows({ nodes, depth, open, toggle, groupBy }) {
  return nodes.map((n) => {
    const isOpen = open.has(n.key);
    return [
      <tr key={n.key} className={`border-b border-white/5 cursor-pointer ${depth === 0 ? "bg-cyan-500/5" : ""} hover:bg-white/5`} onClick={() => toggle(n.key)} data-testid={`scorecard-report-node-${n.key}`}>
        <td className="py-1.5 pr-2" style={{ paddingLeft: 6 + depth * 18 }}>
          {isOpen ? <ChevronDown className="w-3 h-3 inline mr-1" /> : <ChevronRight className="w-3 h-3 inline mr-1" />}
          <span className="text-[#8a94a6] text-[10px] uppercase mr-1">{DIM_LABEL[n.dim]}</span>
          <span className="text-white">{n.label}</span>
          <span className="text-[#8a94a6] text-[10px] ml-1">({n.totals.count})</span>
        </td>
        <Totals t={n.totals} />
      </tr>,
      isOpen && n.children && <Fragment key={`${n.key}-c`}>{NodeRows({ nodes: n.children, depth: depth + 1, open, toggle, groupBy })}</Fragment>,
      isOpen && (n.entries || []).map((e) => (
        <tr key={`${n.key}-${e.id}`} className="border-b border-white/5 text-[11px]" data-testid={`scorecard-report-entry-${e.id}`}>
          <td className="py-1 pr-2 text-white/70" style={{ paddingLeft: 24 + depth * 18 }}>
            {e.date} · {e.kind === "penalty" ? "Penalty" : "Reward"}{e.group ? " (group)" : ""} · {e.reason}
            {!groupBy.includes("person") && <span className="text-[#8a94a6]"> · {e.person}</span>}
            {!groupBy.includes("project") && <span className="text-[#8a94a6]"> · {e.project}</span>}
          </td>
          <td />
          <td className="text-right font-mono px-2 text-rose-300">{e.kind === "penalty" ? money(e.amount) : ""}</td>
          <td className="text-right font-mono px-2 text-emerald-300">{e.kind === "reward" ? money(e.amount) : ""}</td>
          <td />
        </tr>
      )),
      isOpen && n.entries && n.entries.length === 0 && (
        <tr key={`${n.key}-none`}><td className="text-[11px] text-[#8a94a6] py-1" style={{ paddingLeft: 24 + depth * 18 }} colSpan={5}>No rewards or penalties in this period.</td></tr>
      ),
    ];
  });
}

const allKeys = (nodes, out = []) => { nodes.forEach((n) => { out.push(n.key); if (n.children) allKeys(n.children, out); }); return out; };

export default function ScorecardReport() {
  const [opts, setOpts] = useState({ people: [], projects: [] });
  const p0 = presets()[0];
  const [f, setF] = useState({ date_from: p0[1], date_to: p0[2], user_ids: [], project_ids: [], group_by: ["person", "project", "date"], date_unit: "month", budget_mode: "full" });
  const [rep, setRep] = useState(null);
  const [open, setOpen] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const set = (patch) => setF((x) => ({ ...x, ...patch }));

  useEffect(() => { api.get("/scorecard/report/options").then((r) => setOpts(r.data)).catch((e) => toast.error(formatErr(e.response?.data?.detail))); }, []);
  const load = useCallback(async () => {
    setBusy(true);
    try { const { data } = await api.post("/scorecard/report", f); setRep(data); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } finally { setBusy(false); }
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const keys = useMemo(() => (rep ? allKeys(rep.tree) : []), [rep]);
  const toggle = (k) => setOpen((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const setLevel = (i, v) => {
    const g = [...f.group_by];
    if (v === "") g.splice(i);
    else { const j = g.indexOf(v); if (j >= 0 && j !== i) g[j] = g[i]; g[i] = v; }
    set({ group_by: g.filter(Boolean) });
    setOpen(new Set());
  };
  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      const { data } = await api.post("/scorecard/report/pdf", { ...f, expanded: [...open].filter((k) => keys.includes(k)) }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch { toast.error("PDF export failed."); } finally { setPdfBusy(false); }
  };
  const lbl = "text-[10px] uppercase tracking-wider text-[#8a94a6]";
  const chip = (on) => `px-2 py-1 rounded text-[11px] border ${on ? "border-cyan-400 bg-cyan-500/15 text-cyan-200" : "border-white/10 text-white/60"}`;

  return (
    <div className="space-y-3" data-testid="scorecard-report">
      <div className="rounded-lg border border-white/10 p-3 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div><div className={lbl}>From</div><DatePickerInput value={f.date_from} onChange={(v) => set({ date_from: v })} testId="scorecard-report-from" /></div>
          <div><div className={lbl}>To</div><DatePickerInput value={f.date_to} onChange={(v) => set({ date_to: v })} testId="scorecard-report-to" /></div>
          <div className="flex flex-wrap gap-1">
            {presets().map(([l, a, b]) => (
              <button key={l} type="button" className={chip(f.date_from === a && f.date_to === b)} onClick={() => set({ date_from: a, date_to: b })} data-testid={`scorecard-report-preset-${l.replace(/\s+/g, "-").toLowerCase()}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><div className={lbl}>People</div><SearchableMultiSelect value={f.user_ids} onChange={(v) => set({ user_ids: v })} options={opts.people} allLabel="All staff" testId="scorecard-report-people" /></div>
          <div><div className={lbl}>Projects</div><SearchableMultiSelect value={f.project_ids} onChange={(v) => set({ project_ids: v })} options={opts.projects} allLabel="All projects" testId="scorecard-report-projects" /></div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className={lbl}>Group by</span>
          {[0, 1, 2].map((i) => (
            <select key={i} value={f.group_by[i] || ""} disabled={i > f.group_by.length} onChange={(e) => setLevel(i, e.target.value)}
              className="hv-input text-xs py-1 !w-auto" data-testid={`scorecard-report-group-${i}`}>
              {i > 0 && <option value="">—</option>}
              {DIMS.map(([k, l]) => <option key={k} value={k}>{`${i + 1}. ${l}`}</option>)}
            </select>
          ))}
          {f.group_by.includes("date") && (
            <div className="flex gap-1">
              <button type="button" className={chip(f.date_unit === "month")} onClick={() => set({ date_unit: "month" })} data-testid="scorecard-report-unit-month">By month</button>
              <button type="button" className={chip(f.date_unit === "day")} onClick={() => set({ date_unit: "day" })} data-testid="scorecard-report-unit-day">By day</button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={lbl}>Possible reward</span>
          <button type="button" className={chip(f.budget_mode === "full")} onClick={() => set({ budget_mode: "full" })} data-testid="scorecard-report-budget-full">Full project budget</button>
          <button type="button" className={chip(f.budget_mode === "range")} onClick={() => set({ budget_mode: "range" })} data-testid="scorecard-report-budget-range">Only budgets added in these dates</button>
          <div className="ml-auto flex gap-2">
            <button type="button" className="hv-btn hv-btn-ghost text-xs" onClick={() => setOpen(new Set(keys))} data-testid="scorecard-report-expand-all">Unfold all</button>
            <button type="button" className="hv-btn hv-btn-ghost text-xs" onClick={() => setOpen(new Set())} data-testid="scorecard-report-collapse-all">Fold all</button>
            <button type="button" className="hv-btn hv-btn-primary text-xs" disabled={pdfBusy || !rep} onClick={exportPdf} data-testid="scorecard-report-export-pdf">
              {pdfBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />} Export PDF (as shown)
            </button>
          </div>
        </div>
      </div>

      {busy && !rep ? <Loader2 className="w-4 h-4 animate-spin text-cyan-300" /> : rep && (
        <>
          <div className="overflow-x-auto rounded-lg border border-white/10">
            <table className="w-full text-xs" data-testid="scorecard-report-table">
              <thead><tr className="text-[10px] uppercase text-[#8a94a6] border-b border-white/10">
                <th className="text-left p-2">Group</th><th className="text-right px-2">Possible reward</th><th className="text-right px-2">Penalties</th><th className="text-right px-2">Rewards</th><th className="text-right px-2">Balance</th>
              </tr></thead>
              <tbody>
                {rep.tree.length ? <NodeRows nodes={rep.tree} depth={0} open={open} toggle={toggle} groupBy={f.group_by} />
                  : <tr><td colSpan={5} className="p-3 text-[#8a94a6]">No scorecard data for these filters.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="rounded-lg border border-white/10 overflow-x-auto">
            <div className="px-2 pt-2 text-xs font-semibold text-white">Totals per person</div>
            <table className="w-full text-xs" data-testid="scorecard-report-people-totals">
              <thead><tr className="text-[10px] uppercase text-[#8a94a6] border-b border-white/10">
                <th className="text-left p-2">Person</th><th className="text-right px-2">Possible reward</th><th className="text-right px-2">Penalties</th><th className="text-right px-2">Rewards</th><th className="text-right px-2">Balance</th>
              </tr></thead>
              <tbody>
                {rep.people.map((p) => <tr key={p.user_id} className="border-b border-white/5" data-testid={`scorecard-report-person-${p.user_id}`}><td className="p-2 text-white">{p.name}</td><Totals t={p} /></tr>)}
                <tr className="border-t border-cyan-400/40" data-testid="scorecard-report-grand"><td className="p-2 font-bold text-white">Total</td><Totals t={rep.grand} bold /></tr>
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-[#8a94a6]">Balance = possible reward − penalties + rewards. Groups under a date show rewards − penalties when the full project budget is used, because budgets have no date.</div>
        </>
      )}
    </div>
  );
}
